import type {
  CatalogAddonDocument,
  EngagementLineItem,
  EngagementLineUnit,
  ServiceDocument,
  ServiceRequestAddonItem,
  ServiceRequestDocument,
  ServiceRequestItem,
} from '@/types'

interface BuildResult {
  lineItems: EngagementLineItem[]
  discountPercent: number
}

type Offering = ServiceDocument & { id: string }
type Addon = CatalogAddonDocument & { id: string }

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `li_${Math.random().toString(36).slice(2, 10)}`
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

function buildServiceLine(
  svc: ServiceRequestItem,
  offering: Offering | undefined
): EngagementLineItem {
  const desc = svc.subType + (svc.date ? ` — ${svc.date}` : '')
  const meta: EngagementLineItem['meta'] = {}
  if (svc.date)                  meta.date = svc.date
  if (svc.time)                  meta.time = svc.time
  if (svc.location)              meta.location = svc.location
  if (svc.complexity)            meta.complexity = svc.complexity
  if (svc.filmLengthMinutes !== undefined) meta.filmLengthMinutes = svc.filmLengthMinutes
  if (svc.headshots !== undefined)         meta.headshots = svc.headshots
  if (svc.includeAudioRecording !== undefined) meta.includeAudioRecording = svc.includeAudioRecording

  const addonsCost = svc.addons.reduce((s, a) => s + a.lineCost, 0)
  const serviceOnly = svc.lineSubtotal - addonsCost

  let qty = 1
  let unit: EngagementLineUnit = 'flat'
  let unitPrice = serviceOnly

  if (offering) {
    const rate =
      offering.hasComplexity && offering.complexityBase && svc.complexity
        ? offering.complexityBase[svc.complexity] ?? offering.basePrice ?? 0
        : offering.basePrice ?? 0

    switch (offering.priceUnit) {
      case 'perHour': {
        const setup = offering.setupTime ?? 0
        const hours = svc.hours ?? 0
        qty = hours + setup
        unit = 'hour'
        unitPrice = rate
        break
      }
      case 'perMinute':
        qty = svc.filmLengthMinutes ?? 0
        unit = 'minute'
        unitPrice = rate
        break
      case 'perHeadshot':
        qty = svc.headshots ?? 0
        unit = 'headshot'
        unitPrice = rate
        break
      case 'flat':
      default:
        qty = 1
        unit = 'flat'
        unitPrice = rate
        break
    }
  }

  const lineTotal = qty > 0 ? round2(qty * unitPrice) : round2(serviceOnly)

  const item: EngagementLineItem = {
    id: newId(),
    description: desc,
    qty,
    unit,
    unitPrice: round2(unitPrice),
    lineTotal,
    sourceOfferingId: svc.offeringId,
  }
  if (Object.keys(meta).length > 0) item.meta = meta
  return item
}

function buildAddonLine(
  addon: ServiceRequestAddonItem,
  parent: ServiceRequestItem,
  catalogAddon: Addon | undefined
): EngagementLineItem {
  let qty = 1
  let unit: EngagementLineUnit = 'flat'
  let unitPrice = addon.lineCost

  if (catalogAddon) {
    const rate = catalogAddon.rate
    const value = typeof addon.value === 'number' ? addon.value : addon.value ? 1 : 0
    switch (catalogAddon.pricingMode) {
      case 'flat':
        qty = value
        unit = 'each'
        unitPrice = rate
        break
      case 'perHour':
        qty = value * (parent.hours ?? 1)
        unit = 'hour'
        unitPrice = rate
        break
      case 'perMinute':
        qty = value * (parent.filmLengthMinutes ?? 1)
        unit = 'minute'
        unitPrice = rate
        break
    }
  }

  // Final safety: if math doesn't reconcile (catalog rate changed since the
  // request was submitted), fall back to the snapshotted lineCost.
  const computed = round2(qty * unitPrice)
  const lineTotal = qty > 0 && Math.abs(computed - addon.lineCost) < 0.01
    ? computed
    : addon.lineCost

  return {
    id: newId(),
    description: addon.label,
    qty,
    unit,
    unitPrice: round2(unitPrice),
    lineTotal: round2(lineTotal),
    sourceAddonId: addon.addonId,
    isAddon: true,
  }
}

export function buildLineItemsFromRequest(
  request: Pick<ServiceRequestDocument, 'services' | 'discountAppliedPercent'>,
  offerings: Offering[],
  addons: Addon[]
): BuildResult {
  const lineItems: EngagementLineItem[] = []
  const offeringById = new Map(offerings.map((o) => [o.id, o]))
  const addonById   = new Map(addons.map((a) => [a.id, a]))

  for (const svc of request.services) {
    lineItems.push(buildServiceLine(svc, offeringById.get(svc.offeringId)))
    for (const a of svc.addons) {
      lineItems.push(buildAddonLine(a, svc, addonById.get(a.addonId)))
    }
  }

  return {
    lineItems,
    discountPercent: request.discountAppliedPercent ?? 0,
  }
}
